import { Box, Typography, Chip, Avatar } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

const RATING_BADGE_MAP = {
  1: { label: 'Poor',      color: '#EF4444' },
  2: { label: 'Below Avg', color: '#F97316' },
  3: { label: 'Average',   color: '#F59E0B' },
  4: { label: 'Good',      color: '#10B981' },
  5: { label: 'Excellent', color: '#6366F1' },
};

const AVATAR_COLORS = [
  '#FF5200', '#6366F1', '#10B981', '#F59E0B',
  '#EC4899', '#8B5CF6', '#14B8A6', '#F97316',
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const MEDALS = {
  1: {
    border: '#FFD700',
    bg: 'linear-gradient(160deg, #FFFDE7 0%, #FFF8E1 100%)',
    shadow: '0 16px 48px rgba(255,215,0,0.20)',
    iconColor: '#F59E0B',
    Icon: EmojiEventsIcon,
    // Responsive sizes: [mobile, tablet, desktop]
    cardHeight:  { xs: 200, sm: 230, md: 260 },
    baseHeight:  { xs: 40,  sm: 48,  md: 56  },
    flex:        { xs: '0 0 160px', sm: '0 0 210px', md: '0 0 248px' },
    avatarSize:  { xs: 48, sm: 56, md: 64 },
    rankBg: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
    iconSize: { xs: 24, md: 32 },
    nameFontSize: { xs: 12, md: 14 },
    countFontSize: { xs: 26, md: 34 },
    delay: '0.06s',
  },
  2: {
    border: '#B0BEC5',
    bg: 'linear-gradient(160deg, #FAFAFA 0%, #F5F5F5 100%)',
    shadow: '0 10px 32px rgba(0,0,0,0.08)',
    iconColor: '#90A4AE',
    Icon: WorkspacePremiumIcon,
    cardHeight:  { xs: 170, sm: 195, md: 220 },
    baseHeight:  { xs: 28,  sm: 33,  md: 38  },
    flex:        { xs: '0 0 130px', sm: '0 0 168px', md: '0 0 196px' },
    avatarSize:  { xs: 38, sm: 44, md: 52 },
    rankBg: 'linear-gradient(135deg, #B0BEC5 0%, #90A4AE 100%)',
    iconSize: { xs: 18, md: 24 },
    nameFontSize: { xs: 11, md: 13 },
    countFontSize: { xs: 22, md: 28 },
    delay: '0.16s',
  },
  3: {
    border: '#CD7F32',
    bg: 'linear-gradient(160deg, #FFF3E0 0%, #FFF8F0 100%)',
    shadow: '0 10px 32px rgba(205,127,50,0.12)',
    iconColor: '#CD7F32',
    Icon: WorkspacePremiumIcon,
    cardHeight:  { xs: 155, sm: 175, md: 196 },
    baseHeight:  { xs: 22,  sm: 25,  md: 28  },
    flex:        { xs: '0 0 120px', sm: '0 0 158px', md: '0 0 196px' },
    avatarSize:  { xs: 34, sm: 40, md: 48 },
    rankBg: 'linear-gradient(135deg, #CD7F32 0%, #BF6B20 100%)',
    iconSize: { xs: 16, md: 22 },
    nameFontSize: { xs: 10, md: 12 },
    countFontSize: { xs: 20, md: 26 },
    delay: '0.26s',
  },
};

function PodiumCard({ entry, currentUserId }) {
  const m = MEDALS[entry.rank];
  const { Icon } = m;
  const initials    = (entry.userFullName || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const avatarColor = getAvatarColor(entry.userFullName);
  const badge       = RATING_BADGE_MAP[Math.round(entry.avgRatingGiven)] || RATING_BADGE_MAP[3];
  const isMe        = entry._id === currentUserId;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: m.flex,
        minWidth: 0,
        '@keyframes podiumCardIn': {
          '0%':   { opacity: 0, transform: 'translateY(24px) scale(0.97)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        animation: `podiumCardIn 0.42s cubic-bezier(0.22,1,0.36,1) ${m.delay} both`,
      }}
    >
      {/* Card */}
      <Box
        sx={{
          width: '100%',
          height: m.cardHeight,
          background: m.bg,
          border: `2px solid ${m.border}`,
          borderRadius: '16px 16px 0 0',
          boxShadow: m.shadow,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1.5, md: 2.5 },
          gap: { xs: 0.5, md: 0.75 },
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': { transform: 'translateY(-4px)' },
        }}
      >
        <Icon sx={{ fontSize: m.iconSize, color: m.iconColor }} />

        <Avatar
          src={entry.profileImage || undefined}
          sx={{
            width: m.avatarSize, height: m.avatarSize,
            bgcolor: avatarColor,
            fontSize: { xs: 12, md: 18 },
            fontWeight: 800, fontFamily: 'Quicksand',
            border: `2.5px solid ${m.border}`,
          }}
        >
          {initials}
        </Avatar>

        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: 'Quicksand', fontWeight: 800,
              fontSize: m.nameFontSize,
              color: '#1C1C2E', lineHeight: 1.25,
              // Prevent long names from overflowing
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '110px', sm: '140px', md: '210px' },
            }}
          >
            {entry.userFullName}
          </Typography>
          {isMe && (
            <Chip
              label="You"
              size="small"
              sx={{ height: 15, fontSize: 9, fontWeight: 700, bgcolor: '#FF5200', color: '#fff', fontFamily: 'Quicksand', mt: 0.25 }}
            />
          )}
        </Box>

        <Typography
          component="span"
          sx={{
            display: 'block',
            fontFamily: 'Quicksand', fontWeight: 900,
            fontSize: m.countFontSize,
            color: '#FF5200', lineHeight: 1,
          }}
        >
          {entry.totalReviews ?? 0}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Quicksand', fontWeight: 600, fontSize: { xs: 8, md: 9 },
            color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.7, mt: -0.5,
          }}
        >
          reviews
        </Typography>

        <Chip
          label={`⭐ ${entry.avgRatingGiven ?? '—'} · ${badge.label}`}
          size="small"
          sx={{
            fontSize: { xs: 9, md: 10 }, fontWeight: 700, fontFamily: 'Quicksand',
            color: badge.color, bgcolor: `${badge.color}18`,
            border: `1px solid ${badge.color}30`, mt: 0.25,
          }}
        />
      </Box>

      {/* Podium base */}
      <Box
        sx={{
          width: '100%',
          height: m.baseHeight,
          background: m.rankBg,
          borderRadius: '0 0 8px 8px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Typography
          sx={{ fontFamily: 'Quicksand', fontWeight: 800, fontSize: { xs: 10, md: 11 }, color: 'rgba(255,255,255,0.9)', letterSpacing: 0.8 }}
        >
          #{entry.rank}
        </Typography>
      </Box>
    </Box>
  );
}

function EmptySlot({ rank }) {
  const m = MEDALS[rank];
  return (
    <Box
      sx={{
        flex: m.flex,
        minWidth: 0,
        height: { xs: 198, sm: 228, md: m.cardHeight.md + m.baseHeight.md },
        border: `2px dashed ${m.border}50`,
        borderRadius: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 600, fontSize: 11, color: '#D1D5DB', textAlign: 'center', px: 1 }}>
        #{rank} · No reviews yet
      </Typography>
    </Box>
  );
}

export default function PodiumSection({ leaderboard, currentUserId }) {
  const byRank = r => leaderboard.find(e => e.rank === r);
  const slots  = [{ rank: 2 }, { rank: 1 }, { rank: 3 }]; // left, center, right

  return (
    <Box>
      <Typography
        sx={{
          fontFamily: 'Quicksand', fontWeight: 800, fontSize: 11,
          color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1.4, mb: { xs: 2, md: 3 },
        }}
      >
        Top Performers
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          gap: { xs: 1, sm: 1.5, md: 2 },
          overflowX: 'auto',            // graceful scroll if viewport is tiny
          pb: 0.5,                      // space for potential scrollbar
        }}
      >
        {slots.map(({ rank }) => {
          const entry = byRank(rank);
          return entry
            ? <PodiumCard key={entry._id} entry={entry} currentUserId={currentUserId} />
            : <EmptySlot key={`empty-${rank}`} rank={rank} />;
        })}
      </Box>
    </Box>
  );
}
